import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronDown, Loader2, Mail } from 'lucide-react';
import { setSeoMetadata, generateOrganizationSchema, generateWebsiteSchema } from '../lib/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useHeroMedia } from '../hooks/useHeroMedia';
import { useFeaturedWidget } from '../hooks/useFeaturedWidget';
import { useActivityCategories } from '../hooks/useActivityCategories';
import { resolveMediaUrl } from '../lib/mediaUrl';
import { categoryUrl } from '../lib/activityUrls';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { HERO, SERVICES, VALUE_PROPS, ROUTES } from '../data/stjSite';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Newsletter signup - posts a real lead into the CRM (source: newsletter_signup). */
const NewsletterForm = () => {
  const [form, setForm] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setStatus('sending');
    try {
      await axios.post(`${API_URL}/api/leads/`, {
        name: form.name.trim() || form.email.trim(),
        email: form.email.trim(),
        message: 'Newsletter signup from the St John Travel & Life home page.',
        subject: 'Newsletter Signup',
        source: 'newsletter_signup',
      });
      setStatus('done');
      setForm({ name: '', email: '' });
    } catch (err) {
      setStatus('idle');
      setError(err.response?.data?.detail || 'Could not subscribe right now. Please try again.');
    }
  };

  if (status === 'done') {
    return (
      <p className="text-green-700 font-semibold text-center" data-testid="newsletter-success">
        You&apos;re on the list. Thanks for subscribing!
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto" data-testid="newsletter-form">
      <input
        type="text"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Your name (optional)"
        className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
        data-testid="newsletter-name"
      />
      <input
        type="email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder="Your email address"
        required
        className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
        data-testid="newsletter-email"
      />
      <button
        type="submit"
        disabled={status === 'sending'}
        className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
        data-testid="newsletter-submit"
      >
        {status === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
        Subscribe
      </button>
      {error && <p className="text-red-600 text-sm sm:hidden">{error}</p>}
    </form>
  );
};

const HomePage = () => {
  const hero = useHeroMedia();
  const featured = useFeaturedWidget();
  // starred categories from Admin > Tours & Charters drive this grid; the curated
  // SERVICES list is the fallback until categories are starred.
  const favoriteCategories = useActivityCategories({ favoritesOnly: true });
  const { siteName, supportEmail } = useSiteSettings();

  useEffect(() => {
    setSeoMetadata({
      title: 'St John Things To Do | Tours, Excursions & Charters | St John USVI',
      description:
        'Book St John USVI tours, boat charters, kayak excursions, BVI trips, villas and airport transfers. Free concierge service with live online availability.',
      keywords:
        'st john things to do, st john usvi tours, boat charters st john, kayak excursions, bvi tours, st john villa rentals, airport transfers st john, virgin islands excursions',
      canonicalPath: '/',
      ogType: 'website',
      jsonLd: [generateOrganizationSchema(), generateWebsiteSchema()],
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ================= HERO ================= */}
      <section
        className="relative min-h-[90vh] lg:min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="home-hero"
      >
        {/* Base layer: guarantees the hero never renders blank if the media
            fails to load or a browser cannot decode the uploaded video. */}
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100 via-white to-white" />

        {/* Overlay media comes from Developer Settings > Hero Media */}
        {hero.videoUrl ? (
          <video
            key={hero.videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            poster={hero.backgroundImageUrl || undefined}
            data-testid="hero-overlay-video"
          >
            <source src={hero.videoUrl} type={hero.videoMimeType} />
          </video>
        ) : hero.backgroundImageUrl ? (
          <div
            className="absolute inset-0 w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url(${hero.backgroundImageUrl})` }}
            data-testid="hero-overlay-image"
          />
        ) : null}

        {/* Subtle dark tint so white hero type stays legible over the video
            without washing the footage out. */}
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative z-10 w-full px-4 py-28">
          <div className="max-w-5xl mx-auto text-center bg-black/25 backdrop-blur-[2px] rounded-xl px-6 py-12">
            <Link
              to={HERO.eyebrowCta.href}
              className="inline-block mb-8 px-5 py-2 bg-white/95 text-blue-700 font-bold rounded-full text-sm tracking-wide hover:bg-white transition-colors shadow-md"
              data-testid="hero-more-info"
            >
              {HERO.eyebrowCta.label}
            </Link>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold italic text-white tracking-tight drop-shadow-[0_3px_10px_rgba(0,0,0,0.7)]">
              {HERO.heading}
            </h1>

            <Link
              to={HERO.primaryCta.href}
              className="mt-10 inline-block px-8 py-4 bg-red-600 text-white font-bold rounded text-base sm:text-lg hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
              data-testid="hero-primary-cta"
            >
              {HERO.primaryCta.label}
            </Link>

            <h2 className="mt-8 text-lg sm:text-xl lg:text-2xl font-semibold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]">
              {HERO.subheading}
            </h2>
          </div>

          <ChevronDown className="w-9 h-9 mx-auto mt-10 text-white animate-bounce drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]" aria-hidden="true" />
        </div>
      </section>

      {/* ================= FEATURING ================= */}
      {/* Widget: 4 portrait slots managed in Admin > Featuring Widget.
          Empty slots are hidden and the remaining tiles stay centred. */}
      {featured.slots.length > 0 && (
        <section className="py-16 lg:py-20 bg-white" data-testid="home-featuring">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 text-center mb-10 tracking-tight">
              {featured.heading}
            </h2>

            <div className="flex flex-wrap justify-center gap-6 lg:gap-8">
              {featured.slots.map((slot, index) => {
                const tile = (
                  <>
                    <div className="aspect-[4/5] overflow-hidden bg-slate-100">
                      <img
                        src={slot.imageUrl}
                        alt={slot.title || `Featured ${index + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    {slot.title && (
                      <div className="p-4 text-center">
                        <h3 className="text-lg font-bold uppercase tracking-wide text-slate-900 group-hover:text-blue-700 transition-colors">
                          {slot.title}
                        </h3>
                      </div>
                    )}
                  </>
                );

                const shared =
                  'group block w-full max-w-[280px] rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-md hover:shadow-2xl transition-all';
                const testId = `featured-slot-${index + 1}`;

                return slot.link ? (
                  <Link key={testId} to={slot.link} className={shared} data-testid={testId}>
                    {tile}
                  </Link>
                ) : (
                  <div key={testId} className={shared} data-testid={testId}>
                    {tile}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ================= SERVICES GRID ================= */}
      {/* 4 across x 3 rows, photo tiles with the title overlaid - matches the
          live reservestj.com layout. */}
      <section id="bookable-services" className="py-16 lg:py-20 bg-white scroll-mt-24" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 text-center tracking-tight">
            Charters, Tours &amp; Activities in St John
          </h2>
          <p className="font-script text-3xl sm:text-4xl text-cyan-500 text-center mt-4 mb-12">
            Helping You Create Magical Memories of your Dream Vacation...
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {(favoriteCategories.categories.length > 0
              ? favoriteCategories.categories.map((c) => ({
                  key: c.id,
                  title: c.name,
                  href: categoryUrl(c),
                  image: resolveMediaUrl(c.image_url),
                  alt: c.name,
                  testId: c.slug,
                }))
              : SERVICES.map((s) => ({
                  key: s.title,
                  title: s.title,
                  href: s.href,
                  image: s.image,
                  alt: s.alt,
                  testId: s.href,
                }))
            ).map((service) => (
              <article key={service.key} data-testid={`service-card-${service.testId}`}>
                <Link
                  to={service.href}
                  className="group relative block aspect-[4/3] rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all"
                >
                  {service.image ? (
                    <img
                      src={service.image}
                      alt={service.alt}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <span className="absolute inset-0 bg-gradient-to-br from-sky-500 to-blue-700" />
                  )}
                  {/* readability wash under the overlaid title */}
                  <span className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                  <h3 className="absolute inset-0 flex items-center justify-center px-3 text-center text-white text-sm sm:text-base lg:text-lg font-bold uppercase tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]">
                    {service.title}
                  </h3>
                </Link>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to={ROUTES.allToursCharters}
              className="inline-block px-8 py-4 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-colors shadow-lg"
              data-testid="home-all-tours-cta"
            >
              View All Tours &amp; Charters
            </Link>
          </div>
        </div>
      </section>

      {/* ================= PLACES TO STAY ================= */}
      {/* Island photo background + parchment map panel + white compass overlay */}
      <section
        className="relative py-20 lg:py-28 overflow-hidden"
        data-testid="home-places-to-stay"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/stj/villa-bg.webp')" }}
          aria-hidden="true"
        />
        {/* keeps the section readable without hiding the island photo */}
        <div className="absolute inset-0 bg-slate-900/25" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Compass on the LEFT so the water and beach stay visible */}
            <div className="flex justify-center">
              <img
                src="/images/stj/compass-white.png"
                alt="St John Travel &amp; Life compass"
                loading="lazy"
                className="w-full max-w-md drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)]"
                data-testid="villa-compass"
              />
            </div>

            {/* Parchment map panel on the RIGHT */}
            <article
              className="relative rounded-2xl shadow-2xl ring-1 ring-amber-900/25 overflow-hidden"
              data-testid="villa-parchment-panel"
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/images/stj/parchment.webp')" }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-amber-50/40" aria-hidden="true" />

              <div className="relative p-8 sm:p-10">
                <p className="text-amber-900 font-bold tracking-widest uppercase text-xs mb-3">
                  Private Island Packages
                </p>
                <h2 className="text-3xl sm:text-4xl font-extrabold italic uppercase text-[#3b2412] tracking-tight">
                  Places To Stay St John
                </h2>
                <p className="font-script text-2xl sm:text-3xl text-amber-800 mt-2 mb-6">
                  &ldquo; St John Villa &amp; Private Home Rentals &rdquo;
                </p>

                <div className="space-y-4 text-[#4a2f1a] leading-relaxed">
                  <p>
                    St John villa rentals are more than just accommodations; they are your private
                    sanctuary in paradise. These villas offer a unique blend of luxury, privacy, and
                    comfort, providing an ideal setting for a relaxing getaway. Whether you are
                    planning a romantic escape, a family vacation, or a group retreat, you will find a
                    wide range of options to suit your needs and preferences.
                  </p>
                  <p>
                    One of the main advantages of villa rentals in St John, Virgin Islands, is the
                    privacy they offer. Unlike hotels or resorts, where you share amenities with other
                    guests, villas provide you with your own private space &mdash; your own pool,
                    garden, or beachfront without any interruptions.
                  </p>
                  <p className="font-bold">Set up a complimentary call today.</p>
                </div>

                <Link
                  to={ROUTES.bookYourTrip}
                  className="mt-8 inline-block px-8 py-4 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-colors shadow-lg"
                  data-testid="villa-quote-cta"
                >
                  Get Your FREE Villa Rental Quote
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ================= VALUE PROPS ================= */}
      <section className="py-16 lg:py-20 bg-slate-50 border-y border-slate-200" data-testid="home-value-props">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-10">
            {VALUE_PROPS.map((prop) => (
              <div key={prop.title} className="text-center">
                <img
                  src={prop.icon}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="w-16 h-16 mx-auto mb-5 object-contain"
                />
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {prop.href ? (
                    <Link to={prop.href} className="hover:text-blue-700 transition-colors">
                      {prop.title}
                    </Link>
                  ) : (
                    prop.title
                  )}
                </h3>
                <p className="text-slate-600 leading-relaxed">{prop.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= NEWSLETTER ================= */}
      <section className="py-16 lg:py-20 bg-white" data-testid="home-newsletter">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-4">
            Subscribe To Our Newsletter
          </h2>
          <p className="text-slate-600 mb-8 leading-relaxed">
            If you see something missing from our site, please reach out
            {supportEmail ? (
              <>
                {' '}&mdash; email{' '}
                <a href={`mailto:${supportEmail}`} className="text-blue-700 font-semibold hover:underline">
                  {supportEmail}
                </a>
              </>
            ) : null}{' '}
            and let us know what we can add, and we will do our very best to get it in place.
          </p>
          <NewsletterForm />
          {siteName ? (
            <p className="text-slate-500 text-sm mt-6">
              {siteName} &mdash; your free St John concierge.
            </p>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
